/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: APR.DELEGATIONS
 *
 * Out-of-office / delegation management (engine E2). A user delegates their
 * approval authority to another user for a date range (optionally scoped to one
 * entity). Backed by apr_set_delegation / apr_cancel_delegation and the
 * apr_delegation table (RLS shows the caller their own delegations).
 */

/* rbac-affordance-ok: self-service. apr_set_delegation takes the delegator
   from csm_current_user_id() — a user can only delegate their OWN approvals —
   and apr_cancel_delegation refuses anyone who is neither the owner nor an
   admin (APR.NOT_OWNER). Neither is page-scoped. */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Search, RefreshCw } from 'lucide-react';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import type { AuthSession } from '../../types/auth';

interface DelegationRow {
  apr_delegation_id: number;
  delegator_user_id: number;
  delegate_user_id: number;
  valid_from: string;
  valid_to: string | null;
  scope_entity: string | null;
  is_active: 'Y' | 'N';
}
interface UserRow { user_id: number; display_name: string; email: string }

const DELEGATION_GRID_COLS: GridColumnDef[] = [
  { key: 'delegate',     header: 'Delegate', defaultWidth: 240 },
  { key: 'valid_from',   header: 'From',     defaultWidth: 180 },
  { key: 'valid_to',     header: 'To',       defaultWidth: 180 },
  { key: 'scope_entity', header: 'Scope',    defaultWidth: 160 },
  { key: 'is_active',    header: 'Active',   defaultWidth: 90  },
];

export interface ApprovalDelegationsPageProps { session: AuthSession; }

export function ApprovalDelegationsPage({ session }: ApprovalDelegationsPageProps) {
  return (
    <PersonalizationProvider pageCode="APR.DELEGATIONS" session={session}>
      <ApprovalDelegationsPageInner session={session} />
    </PersonalizationProvider>
  );
}

function ApprovalDelegationsPageInner({ session }: ApprovalDelegationsPageProps) {
  const { t } = useTranslation();
  const myId = session.effective_user.user_id;
  const [rows, setRows] = useState<DelegationRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // §17.B5: per-column multi-value facets (logical columns of the list).
  const [facets, setFacets] = useState<{ scope: string[]; active: string[] }>({ scope: [], active: ['Y'] });
  const detail = useExpandedRows<number>();

  const [delegate, setDelegate] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [scopeEntity, setScopeEntity] = useState('');

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.from('apr_delegation')
        .select('apr_delegation_id, delegator_user_id, delegate_user_id, valid_from, valid_to, scope_entity, is_active')
        .order('apr_delegation_id', { ascending: false });
      if (e) throw e;
      setRows((data ?? []) as DelegationRow[]);
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  useEffect(() => {
    void reload();
    void apiDb.from('csm_user_master').select('user_id, display_name, email').eq('is_active', 'Y').order('display_name')
      .then(({ data }) => setUsers((data ?? []) as UserRow[]));
  }, []);

  const create = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      if (!delegate) throw new Error(t('apr_deleg.err.choose_delegate', 'Choose a delegate.'));
      const { data, error: e } = await apiDb.rpc('apr_set_delegation', {
        p_delegate_user_id: Number(delegate),
        p_valid_from: validFrom || null,
        p_valid_to: validTo || null,
        p_scope_entity: scopeEntity.trim() || null,
      });
      if (e) throw e;
      const res = data as { ok: boolean; error_code?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'APR.FAILED');
      setNotice(t('apr_deleg.created', 'Delegation created.')); setDelegate(''); setValidFrom(''); setValidTo(''); setScopeEntity('');
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const cancel = async (id: number) => {
    setBusy(true); setError(null);
    try {
      const { data, error: e } = await apiDb.rpc('apr_cancel_delegation', { p_delegation_id: id });
      if (e) throw e;
      const res = data as { ok: boolean; error_code?: string };
      if (!res?.ok) throw new Error(res?.error_code ?? 'APR.FAILED');
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const nameOf = (uid: number) => users.find(u => u.user_id === uid)?.display_name ?? `user ${uid}`;

  // §17.B13 related children (expand/contract) — the delegation's delegate and
  // delegator parties, resolved to names via nameOf (the raw user id rides in `title`).
  const buildRelatedGroups = (id: number): RelatedChildGroup[] => {
    const row = rows.find(r => r.apr_delegation_id === id);
    if (!row) return [];
    return [
      { key: 'delegate',  title: t('apr_deleg.related.delegate', 'Delegate'),  items: [{ id: row.delegate_user_id,  label: nameOf(row.delegate_user_id),  title: String(row.delegate_user_id) }] },
      { key: 'delegator', title: t('apr_deleg.related.delegator', 'Delegator'), items: [{ id: row.delegator_user_id, label: nameOf(row.delegator_user_id), title: String(row.delegator_user_id) }] },
    ];
  };
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  // Distinct facet options derived from the loaded rows (§17.B5). Scope facet
  // surfaces the "all entities" rows under an explicit value.
  const SCOPE_ALL = '__ALL__';
  const facetOptions = useMemo(() => {
    const scopes = [...new Set(rows.map(r => r.scope_entity).filter((s): s is string => !!s))].sort();
    return {
      scope: [
        ...(rows.some(r => !r.scope_entity) ? [{ value: SCOPE_ALL, label: t('apr_deleg.all', 'all') }] : []),
        ...scopes.map(v => ({ value: v, label: v })),
      ],
      active: [{ value: 'Y', label: t('common.boolean.active', 'Active') }, { value: 'N', label: t('common.boolean.inactive', 'Inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.scope.length + facets.active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ scope: [], active: [] }); };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    // OR within a facet, AND across facets.
    const matchesFacets = (r: DelegationRow) =>
      (facets.scope.length  === 0 || facets.scope.includes(r.scope_entity ?? SCOPE_ALL)) &&
      (facets.active.length === 0 || facets.active.includes(r.is_active));
    const base = !q ? rows : rows.filter(r =>
      nameOf(r.delegate_user_id).toLowerCase().includes(q) ||
      nameOf(r.delegator_user_id).toLowerCase().includes(q) ||
      (r.scope_entity ?? '').toLowerCase().includes(q)
    );
    return base.filter(matchesFacets);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, facets, users]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('apr_deleg.title', 'My Approval Delegations')}
          description={t('apr_deleg.list.description', 'Hand your approval authority to another user for a date range.')}
          actions={
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
              <RefreshCw className="h-3.5 w-3.5" /> {t('apr_deleg.refresh', 'Refresh')}
            </button>
          }
        />

        {error && (
          <ErrorBanner
            message={t('apr_deleg.load_failed', "Couldn't complete the delegation operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <div className="rounded border border-csm-border bg-white p-3">
          <div className="mb-2 text-csm-sm font-semibold text-csm-ink-soft">{t('apr_deleg.delegate_heading', 'Delegate my approvals to…')}</div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <PersonalizedField componentKey="modal" fieldKey="delegate" label={t('apr_deleg.field.delegate', 'Delegate')}>
              <FilteredModalSingleSelect
                label={t('apr_deleg.field.delegate', 'Delegate')}
                options={users.filter(u => u.user_id !== myId).map(u => ({ internal_code: String(u.user_id), display_text: u.display_name }))}
                selectedCode={delegate || null}
                onChange={(code) => setDelegate(code ?? '')}
                clearLabel={t('apr_deleg.choose', '— choose —')}
              />
            </PersonalizedField>
            <PersonalizedField componentKey="modal" fieldKey="valid_from" label={t('apr_deleg.field.from', 'From')}>
              <label className="text-csm-xs text-csm-ink-soft">{t('apr_deleg.field.from', 'From')}
                <input type="datetime-local" value={validFrom} onChange={e => setValidFrom(e.target.value)} className="mt-1 h-8 w-full rounded border border-csm-border px-2 text-csm-sm" />
              </label>
            </PersonalizedField>
            <PersonalizedField componentKey="modal" fieldKey="valid_to" label={t('apr_deleg.field.to', 'To (optional)')}>
              <label className="text-csm-xs text-csm-ink-soft">{t('apr_deleg.field.to', 'To (optional)')}
                <input type="datetime-local" value={validTo} onChange={e => setValidTo(e.target.value)} className="mt-1 h-8 w-full rounded border border-csm-border px-2 text-csm-sm" />
              </label>
            </PersonalizedField>
            <PersonalizedField componentKey="modal" fieldKey="scope_entity" label={t('apr_deleg.field.scope', 'Scope entity (optional)')}>
              <label className="text-csm-xs text-csm-ink-soft">{t('apr_deleg.field.scope', 'Scope entity (optional)')}
                <input type="text" value={scopeEntity} onChange={e => setScopeEntity(e.target.value)} placeholder={t('apr_deleg.scope_placeholder', 'all entities')} className="mt-1 h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm" />
              </label>
            </PersonalizedField>
          </div>
          <button onClick={() => void create()} disabled={busy} className="mt-3 rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{t('apr_deleg.create', 'Create delegation')}</button>
        </div>

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('apr_deleg.filter_placeholder', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('apr_deleg.col.scope', 'Scope')} options={facetOptions.scope} selected={facets.scope} onChange={v => setFacets(f => ({ ...f, scope: v }))} />
            <FacetFilter label={t('apr_deleg.col.active', 'Active')} options={facetOptions.active} selected={facets.active} onChange={v => setFacets(f => ({ ...f, active: v }))} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('apr_deleg.working', 'Working…')}</span>}
              {notice && <span className="text-csm-xs text-primary">{notice}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={DELEGATION_GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-2 py-1 text-left" title={t('apr_deleg.related.header', 'Related records')} />
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-2 py-1 text-left"
                        style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-2 py-1 text-left" style={{ width: '1%' }}>{t('apr_deleg.col.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <Fragment key={r.apr_delegation_id}>
                  <tr className="border-t border-csm-border-soft">
                    <td className="px-2 py-1 text-center align-middle" onClick={e => e.stopPropagation()}>
                      <RelatedExpandButton open={detail.isOpen(r.apr_delegation_id)} hasChildren={hasRelated(r.apr_delegation_id)} onToggle={() => detail.toggle(r.apr_delegation_id)} expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="px-2 py-1"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'delegate'     && <>{nameOf(r.delegate_user_id)}{r.delegator_user_id !== myId && <span className="ml-1 text-csm-2xs text-csm-watermark">{t('apr_deleg.from_label', '(from {{name}})', { name: nameOf(r.delegator_user_id) })}</span>}</>}
                        {col.key === 'valid_from'   && <span className="text-csm-sm">{new Date(r.valid_from).toLocaleString()}</span>}
                        {col.key === 'valid_to'     && <span className="text-csm-sm">{r.valid_to ? new Date(r.valid_to).toLocaleString() : <span className="text-csm-watermark">{t('apr_deleg.open_ended', 'open-ended')}</span>}</span>}
                        {col.key === 'scope_entity' && <span className="font-mono text-csm-xs">{r.scope_entity ?? t('apr_deleg.all', 'all')}</span>}
                        {col.key === 'is_active'    && <YesNoBadge value={r.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                      </td>
                    ))}
                    <td className="px-2 py-1 text-right">
                      {r.is_active === 'Y' && r.delegator_user_id === myId && (
                        <button onClick={() => void cancel(r.apr_delegation_id)} className="rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger">{t('apr_deleg.cancel', 'Cancel')}</button>
                      )}
                    </td>
                  </tr>
                  {detail.isOpen(r.apr_delegation_id) && (
                    <tr><td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}><RelatedChildrenPanel groups={buildRelatedGroups(r.apr_delegation_id)} session={session} /></td></tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && <tr><td colSpan={visibleCols.length + 2} className="px-2 py-3"><EmptyState variant={anyFilterActive ? 'no-match' : 'no-data'} title={t('apr_deleg.empty', 'No delegations.')} /></td></tr>}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
